<template>
  <div class="profile-page">
    <slot name="header"></slot>
    <div class="container">
      <!-- Desktop sidebar -->
      <div class="side desktop-side">
        <slot name="side"></slot>
      </div>
      
      <!-- Mobile menu trigger -->
      <div class="mobile-menu-trigger" @click="toggleMobileMenu">
        <span class="mobile-menu-title">{{ currentPageTitle }}</span>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path d="M6 9L12 15L18 9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      
      <!-- Mobile bottom sheet -->
      <Teleport to="body">
        <Transition name="bottom-sheet">
          <div v-if="mobileMenuOpen" class="bottom-sheet-overlay" @click="closeMobileMenu">
            <div class="bottom-sheet" @click.stop>
              <div class="bottom-sheet-handle"></div>
              <div class="bottom-sheet-header">
                 <span>{{ $t('Меню') }}</span>
                <button class="bottom-sheet-close" @click="closeMobileMenu">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M6 18L18 6M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                  </svg>
                </button>
              </div>
              <div class="bottom-sheet-content">
                <slot name="side"></slot>
              </div>
            </div>
          </div>
        </Transition>
      </Teleport>
      
      <div class="content">
        <slot name="content"></slot>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PersonalLayout',
  data() {
    return {
      mobileMenuOpen: false
    }
  },
  computed: {
    currentPageTitle() {
      const path = this.$route?.path || '';
       if (path.includes('security')) return this.$t('nav.security');
       if (path.includes('objects')) return this.$t('nav.objects');
       if (path.includes('subscription')) return this.$t('nav.subscription');
       return this.$t('common.account');
    }
  },
  methods: {
    toggleMobileMenu() {
      this.mobileMenuOpen = !this.mobileMenuOpen;
    },
    closeMobileMenu() {
      this.mobileMenuOpen = false;
    }
  },
  watch: {
    '$route'() {
      this.mobileMenuOpen = false;
    }
  }
}
</script>

<style scoped>
.profile-page {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: #faf9f7;
}

.container {
  display: flex;
  flex: 1;
  padding: 0 20px;
  overflow: hidden;
  gap: 0 10px;
}

.desktop-side {
  box-shadow: 3px 3px 4px 0 rgba(178, 176, 173, 0.2);
  background: #fff;
  border: 1px solid #fafafa;
  border-radius: 10px;
  padding: 25px 10px;
  width: 255px;
  flex-shrink: 0;
}

.content {
  flex: 1;
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.content::-webkit-scrollbar {
  display: none;
}

.mobile-menu-trigger {
  display: none;
}

/* Bottom Sheet Styles */
.bottom-sheet-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  display: flex;
  align-items: flex-end;
}

.bottom-sheet {
  background: #fff;
  border-radius: 20px 20px 0 0;
  width: 100%;
  max-height: 70vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.bottom-sheet-handle {
  width: 40px;
  height: 4px;
  background: #ddd;
  border-radius: 2px;
  margin: 12px auto 8px;
}

.bottom-sheet-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 20px 16px;
  border-bottom: 1px solid #eee;
}

.bottom-sheet-header span {
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.bottom-sheet-close {
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: #666;
  display: flex;
  align-items: center;
  justify-content: center;
}

.bottom-sheet-content {
  padding: 16px 20px 32px;
  overflow-y: auto;
}

/* Bottom sheet transitions */
.bottom-sheet-enter-active,
.bottom-sheet-leave-active {
  transition: opacity 0.3s ease;
}

.bottom-sheet-enter-active .bottom-sheet,
.bottom-sheet-leave-active .bottom-sheet {
  transition: transform 0.3s ease;
}

.bottom-sheet-enter-from,
.bottom-sheet-leave-to {
  opacity: 0;
}

.bottom-sheet-enter-from .bottom-sheet,
.bottom-sheet-leave-to .bottom-sheet {
  transform: translateY(100%);
}

@media (max-width: 768px) {
  .container {
    flex-direction: column;
    padding: 0;
    position: relative;
  }

  .desktop-side {
    display: none;
  }

  .mobile-menu-trigger {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    background: #fff;
    border-bottom: 1px solid #eee;
    cursor: pointer;
    user-select: none;
  }

  .mobile-menu-title {
    font-size: 16px;
    font-weight: 600;
    color: #496782;
  }

  .mobile-menu-trigger svg {
    color: #496782;
  }

  .content {
    padding: 16px;
  }

  /* Bottom sheet menu items */
  .bottom-sheet-content :deep(.side-slide) {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 16px;
    font-size: 16px;
    font-weight: 500;
    color: #333;
    text-decoration: none;
    border-radius: 12px;
    transition: all 0.2s;
    margin-bottom: 4px;
  }

  .bottom-sheet-content :deep(.side-slide svg) {
    color: #666;
    flex-shrink: 0;
  }

  .bottom-sheet-content :deep(.side-slide.active) {
    background: #f0f4f8;
    color: #496782;
  }

  .bottom-sheet-content :deep(.side-slide.active svg) {
    color: #496782;
  }

  .bottom-sheet-content :deep(.side-slide:hover) {
    background: #f5f7fa;
  }

  .bottom-sheet-content :deep(.logout-slide) {
    color: #e74c3c;
    margin-top: 12px;
    padding-top: 16px;
    border-top: 1px solid #eee;
  }

  .bottom-sheet-content :deep(.logout-slide svg) {
    color: #e74c3c;
  }

  .bottom-sheet-content :deep(.logout-slide:hover) {
    background: #fef2f2;
  }
}

.desktop-side :deep(.side-slide) {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  font-weight: 400;
  font-size: 15px;
  line-height: 120%;
  color: #555;
  cursor: pointer;
  transition: all 0.2s;
  border-radius: 8px;
  border: 1px solid transparent;
  text-decoration: none;
}

.desktop-side :deep(.side-slide svg) {
  flex-shrink: 0;
  color: #888;
  transition: color 0.2s;
}

.desktop-side :deep(.side-slide:hover) {
  background: #f5f7fa;
  color: #333;
}

.desktop-side :deep(.side-slide:hover svg) {
  color: #555;
}

.desktop-side :deep(.side-slide.active) {
  background: #f0f4f8;
  border-color: #e0e6ed;
  color: #496782;
  font-weight: 500;
}

.desktop-side :deep(.side-slide.active svg) {
  color: #496782;
}

.desktop-side :deep(.side-slide + .side-slide) {
  margin-top: 5px;
}

.desktop-side :deep(.logout-slide) {
  cursor: pointer;
  color: #e74c3c;
  margin-top: 20px;
  padding-top: 15px;
  border-top: 1px solid #eee;
}

.desktop-side :deep(.logout-slide:hover) {
  color: #c0392b;
  background: #fef2f2;
}

.desktop-side :deep(.logout-slide svg) {
  color: #e74c3c;
}
</style>
